import React, { useContext, useEffect, useState } from "react";
import { Button } from 'primereact/button';
import { apiRequest, parseDataToAPI, formatFileSize, toast, url } from "@/Helpers/general";
import TextInputTemplate from "@/Components/Templates/Forms/TextInputTemplate";
import DropdownInputTemplate from "@/Components/Templates/Forms/DropdownInputTemplate";
import { getFormErrorMessage, isFormFieldValid, validateForm } from "@/Helpers/forms";
import { AppContext } from "@/Components/Context/AppContext";
import { createStorageObject } from "@/Helpers/storage";
import FileUploadTemplate from "@/Components/Templates/Forms/FileUploadTemplate";
import { Accordion, AccordionTab } from 'primereact/accordion';
import { fieldsPlaceholders } from "@/Config/forms";
import DateInputTemplate from "@/Components/Templates/Forms/DateInputTemplate";
import NumberInputTemplate from "@/Components/Templates/Forms/NumberInputTemplate";
import { CLAIM_DOCUMENTS_ALLOWED_EXTENSIONS } from "@/Config/constants";
import AddChoModal from "@/Components/Modals/AddChoModal";
import { paymentPackFields } from "@/Config/forms/payment-pack";
import moment from "moment";
import { Img } from "react-image";

/**
 * Component which handles the "manual input" tab of creating a new claim
 */
const NewClaimManualForm = () => {
    const { toastRef } = useContext(AppContext);
    const booleanOptions = [
        {
            label: 'No',
            value: 0
        },
        {
            label: 'Yes',
            value: 1
        }
    ];

    const [activeAccordionIndex, setActiveAccordionIndex] = useState(0);
    const [formData, setFormData] = useState();
    const [formValidationRules, setFormValidationRules] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [formLoading, setFormLoading] = useState(false);
    const [files, setFiles] = useState();
    const [canSave, setCanSave] = useState(false);
    const [choOptions, setChoOptions] = useState([]);
    const [showAddChoModal, setShowAddChoModal] = useState(false);
    const [visibleDateRanges, setVisibleDateRanges] = useState({});

    /**
     * Update the state of the form data (when changing an input value)
     *
     * @param {string} name the name of the input
     * @param {string} value the new value of the input
     */
    const changeFormData = (name, value) => {
        let getFormData = { ...formData },
            allFields = [];

        paymentPackFields.forEach((subcategory) => {
            subcategory.fields.forEach((config) => {
                allFields = allFields.concat(config);
            });
        });

        let fieldConfig = allFields.find(field => field.name == name);

        if (fieldConfig) {
            // check if this field is a dependency source
            if (fieldConfig.dependencySource && value !== fieldConfig.dependencyMatchValue) {
                allFields.forEach((field) => {
                    // if dependent fields will be hidden, clear their values
                    if (field.dependency && field.dependency.type == 'visibility' && field.dependency.sourceField == name) {
                        getFormData[field.name] = '';
                    }
                });
            }

            if (fieldConfig.calculate) {
                // check if this fields is a date range and calculate the number of days selected
                if (fieldConfig.type == 'date_range_start' && fieldConfig.calculate?.type == 'days') {
                    if (Array.isArray(value) && value[0] && value[1]) {
                        let startDate = moment(value[0]),
                            endDate = moment(value[1]),
                            daysBetween,
                            totalDays = 0;

                        if (startDate.isValid() && endDate.isValid()) {
                            daysBetween = startDate.diff(endDate, 'days');
                        }

                        if ((daysBetween || daysBetween === 0) && fieldConfig.calculate?.targetField) {
                            getFormData[fieldConfig.calculate?.targetField] = Math.abs(daysBetween) + 1;

                            totalDays += Math.abs(daysBetween) + 1;
                        }

                        // go thru each number of days field and calculate the total
                        allFields.forEach((item) => {
                            if (item.name != fieldConfig.calculate?.targetField
                                && item?.calculate?.group == fieldConfig?.dateRange?.key
                                && getFormData[item.name]) {
                                    totalDays += parseInt(getFormData[item.name]);
                            }
                        });

                        getFormData[fieldConfig.calculate?.totalField] = totalDays;
                    }
                }

                // check if this is a number of days field and update the total amount of days field
                if (fieldConfig.calculate?.type == 'days' && fieldConfig.calculate?.group) {
                    // go thru each number of days field and update the total
                    let totalDaysNo = 0;

                    allFields.forEach((fieldItem) => {
                        if (fieldItem.name == fieldConfig.name) {
                            totalDaysNo += value;

                        } else {
                            if (fieldItem?.calculate?.group == fieldConfig?.calculate?.group
                                && getFormData[fieldItem.name]) {
                                    totalDaysNo += parseInt(getFormData[fieldItem.name]);
                            }
                        }
                    });

                    getFormData[fieldConfig.calculate?.totalField] = totalDaysNo;
                }
            }
        }

        getFormData[name] = value;

        setFormData(getFormData);
    };

    /**
     * Make another date range field visible
     *
     * @param {object} field object with field data
     */
    const addDateRange = (field) => {
        let copyVisibleDateRanges = { ...visibleDateRanges };

        copyVisibleDateRanges[field.dateRange.key] = field.dateRange.index;

        setVisibleDateRanges(copyVisibleDateRanges);
    };

    const generateInputField = (field) => {
        switch (field.type) {
            case 'text':
                return (
                    <TextInputTemplate name={field.name} label={field.label} placeholder={field.custom_placeholder || fieldsPlaceholders.text}
                        value={formData ? (formData[field.name] || '') : ''}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        disabled={field.disabled}
                    />
                );

            case 'date':
                return (
                    <DateInputTemplate name={field.name} label={field.label} placeholder={field.custom_placeholder || fieldsPlaceholders.date}
                        value={formData ? (formData[field.name] || '') : ''}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        disabled={field.disabled}
                    />
                );

            case 'date_range_start':
                return (<>
                    {(field.dateRange.index == 0
                        || (field.dateRange.index <= visibleDateRanges[field.dateRange.key])
                        || (formData && formData[field.name])) && <DateInputTemplate
                            name={field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.dateRange}
                            labelClassName="one-line"
                            value={formData ? (formData[field.name] || '') : ''}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, field.name)}
                            errorMessage={getFormErrorMessage(formErrors, field.name)}
                            disabled={field.disabled}
                            selectionMode="range"
                        />}
                    {field.dateRange.index == visibleDateRanges[field.dateRange.key] + 1 &&
                        <div className="mb-3 relative">
                            <label htmlFor={field.name + '-add-btn'} className="w-full block mb-2 pr-4 one-line">&nbsp;</label>
                            <Button className="grey-nav-btn grey-nav-btn-hover" label="Add hire data" onClick={() => addDateRange(field)} icon="pi pi-plus" />
                        </div>}
                </>);
                break;

            case 'boolean':
                return (
                    <DropdownInputTemplate name={field.name} label={field.label} placeholder={field.custom_placeholder || fieldsPlaceholders.boolean}
                        value={formData ? formData[field.name] : ''}
                        options={booleanOptions}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                    />
                );

            case 'integer':
                return (
                    <NumberInputTemplate name={field.name} label={field.label} placeholder={field.custom_placeholder || fieldsPlaceholders.integer}
                        value={formData ? (formData[field.name] || null) : null}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        disabled={field.disabled}
                    />
                );

            case 'float':
                return (
                    <NumberInputTemplate name={field.name} label={field.label} placeholder={field.custom_placeholder || fieldsPlaceholders.integer}
                        labelClassName="one-line"
                        value={formData ? (formData[field.name] || null) : null}
                        mode={'decimal'}
                        bottomMargin={3}
                        format
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        disabled={field.disabled}
                    />
                );

            default:
                break;
        }
    };

    /**
     * Submits the new claim form
     */
    const submitForm = async () => {
        setFormLoading(true);

        // parse date ranges
        let copyFormData = { ...formData };

        paymentPackFields.forEach((category) => {
            category.fields.forEach((field) => {
                if (field.type == 'date_range_start') {
                    if (Array.isArray(copyFormData[field.name])) {
                        let copyDatesArray = [...copyFormData[field.name]];

                        if (copyDatesArray[0]) {
                            copyFormData[field.name] = copyDatesArray[0];
                        }

                        if (copyDatesArray[1]) {
                            copyFormData[field.endKey] = copyDatesArray[1];
                        }
                    }
                }
            });
        });

        let validation = validateForm(formValidationRules, formData, setFormErrors),
            fullFormData = new FormData(),
            parseFormData = parseDataToAPI(copyFormData, 'payment_pack');

        if (files) {
            files.forEach((file, i) => {
                fullFormData.append('file-' + i, file);
            });
        }

        for (const key in parseFormData) {
            fullFormData.append(key, parseFormData[key]);
        }

        if (validation) {
            apiRequest('post', 'request', {
                data: fullFormData,
                onSuccess: (response) => {
                    setFormLoading(false);

                    if (response?.data?.responseType == 'success') {
                        createStorageObject('flashMessages', [{
                            type: 'success',
                            title: 'Success',
                            message: 'The claim has been created.'
                        }]);

                        window.location.replace(url('home'));

                    } else {
                        toast(toastRef, 'error', 'Error creating claim.');
                    }
                },
                onFailure: (error) => {
                    setFormLoading(false);

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};

                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }

                            setFormErrors(newFormErrors);

                        } else {
                            toast(toastRef, 'error', 'Error creating claim.');
                        }

                    } else {
                        toast(toastRef, 'error', 'Error creating claim.');
                    }
                }
            });

        } else {
            setFormLoading(false);
        }
    };

    /**
     * Add files in the state
     *
     * @param {object} event data related to the files
     */
    const uploadHandler = (event) => {
        setFiles(event.files);
    };

    /**
     * Remove a file from the state
     *
     * @param {object} event data related to the file
     */
    const onFileRemove = (event) => {
        let copyFiles = [...files];

        if (event?.file?.objectURL) {
            copyFiles = copyFiles.filter((file) => file.objectURL != event.file.objectURL);
        }

        setFiles(copyFiles);
    };

     /**
     * Generate a preview of a file
     *
     * @param {object} doc data related to the file
     */
	const filePreview = (doc, uploadPreview = false) => {
		let preview = "";
		let type = uploadPreview ? doc?.type : doc?.file_mime;

		switch (type) {
			case "application/pdf":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-pdf"></i>
					</div>
				);
				break;
			case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-word"></i>
					</div>
				);
				break;
			case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-excel"></i>
					</div>
				);
				break;
			case "application/msword":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-word"></i>
					</div>
				);
				break;
			case "application/vnd.ms-excel":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-excel"></i>
					</div>
				);
				break;
			default:
				preview = (
					<div className="no-picture">
						<i className="pi pi-file"></i>
					</div>
				);
				break;
		}

		if (type.search("image") !== -1) {
			let getImg = uploadPreview ? doc.objectURL : imgUrls.find((img) => img.id == doc.id);

			if (getImg) {
				preview = (
					<Img
						src={getImg.url}
						alt=""
						unloader={
							<div className="no-picture">
								<i className="pi pi-image"></i>
							</div>
						}
					/>
				);
			}
		}

		return preview;
	};

    /**
	 * The preview (for files other than images) which appears after you select a file
	 */
	const itemTemplate = (file, event) => {
		return (
			<div className="p-fileupload-row documents-files-format">
				<div className="file-extension-icon">{filePreview(file, true)}</div>

				<div className="p-fileupload-filename">{file.name}</div>

				<div>{formatFileSize(file.size)}</div>

				<div>
					<button
						type="button"
						className="p-button p-component p-button-icon-only"
						onClick={() => {
							event.onRemove();
						}}
					>
						<span className="p-button-icon p-c pi pi-times"></span>
						<span className="p-button-label p-c">&nbsp;</span>
					</button>
				</div>
			</div>
		);
	};

    useEffect(() => {
        // set initial form data and validation rules
        let initialFormData = { cho_id: '' },
            validationRules = [{
                field: 'cho_id',
                rules: [{ name: 'required' }]
            }],
            dateRanges = {};

        paymentPackFields.forEach((category) => {
            return category.fields.forEach((field) => {
                initialFormData[field.name] = '';

                if (field.type == 'date_range_start') {
                    if (typeof (dateRanges[field.dateRange.key]) == 'undefined') {
                        dateRanges[field.dateRange.key] = 0; // here we will save the index of the last visible date range from the group
                    }
                }

                if (field.modalValidation && Object.keys(field.modalValidation).length > 0) {
                    validationRules.push({
                        field: field.name,
                        rules: field.modalValidation
                    });
                }
            });
        });

        setFormData(initialFormData);
        setFormValidationRules(validationRules);
        setVisibleDateRanges(dateRanges);

        // get chos list
        apiRequest('get', 'chos', {
            data: { limit: 8008135 },
            onSuccess: (response) => {
                let chos = [];

                response?.result.forEach((cho) => {
                    chos.push({
                        label: cho.name,
                        value: cho.id
                    });
                });

                setChoOptions(chos);
            },
            onFailure: () => { }
        });
    }, []);

    useEffect(() => {
        if (formData) {
            let hasValues = false;

            for (let key in formData) {
                if (formData[key] !== '' && formData[key] !== null) {
                    hasValues = true;
                }
            }

            setCanSave(hasValues);
        }

    }, [formData]);

    useEffect(() => {
        if (files) {
            setCanSave(files.length > 0);
        }

    }, [files]);

    useEffect(() => {
        // jump to the first available error (if we have any)
        if (formErrors) {
            let getFirstErrorLabel = document.querySelector('label[for="' + Object.keys(formErrors)[0] + '"]');

            if (getFirstErrorLabel) {
                getFirstErrorLabel.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }

    }, [formErrors]);

    /**
     * Check if a field wrapper and it's contents can be shown or not
     *
     * @param {object} fields object with field data
     */
    const canShowField = (field) => {
        return field.type != 'date_range_end' && field.type != 'date_range_start' && !field.group && !field.dependency // all other field types
            // fields which are date range
            || (field.type == 'date_range_start'
                && (field.dateRange.index <= visibleDateRanges[field.dateRange.key] + 1)
            )
            // fields which are in the same group
            || (field.group
                && (field.group.index <= visibleDateRanges[field.group.key])
            )
            // fields which depend on other field's value
            || (field.dependency && field.dependency.type == 'visibility'
                && formData && formData[field.dependency.sourceField] === field.dependency.matchValue);
    };

    return (
        <>
            <div className="general-form">
                <div className="grid">
                    <div className="col-6">
                        <label htmlFor="" className="block mb-2">Files upload:</label>
                        <FileUploadTemplate itemTemplate={itemTemplate} uploadHandler={uploadHandler} onFileRemove={onFileRemove} accept={CLAIM_DOCUMENTS_ALLOWED_EXTENSIONS.join(',')} />
                    </div>

                    <div className="col-6">
                        <DropdownInputTemplate name={'cho_id'} label={'Select CHO'} placeholder={fieldsPlaceholders.boolean}
                            value={formData ? formData['cho_id'] : ''}
                            options={choOptions}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, 'cho_id')}
                            errorMessage={getFormErrorMessage(formErrors, 'cho_id')}
                        />

                        <Button icon="pi pi-plus" label="Add new CHO" onClick={() => setShowAddChoModal(true)} />
                        <AddChoModal visible={showAddChoModal}
                            setVisible={setShowAddChoModal}
                            choOptions={choOptions}
                            setChoOptions={setChoOptions}
                            claimFormData={formData}
                            setClaimFormData={setFormData}
                        />
                    </div>
                </div>

                <Accordion activeIndex={activeAccordionIndex} onTabChange={(e) => setActiveAccordionIndex(e.index)} className="my-5">
                    {paymentPackFields.map((category) => {
                        return (
                            <AccordionTab header={category.name} key={category.key}>
                                <div className="formgrid grid my-3">
                                    <div className="col-12">
                                        <div className="grid">
                                            {category.fields.map((field) => {
                                                if (canShowField(field)) {
                                                    return (
                                                        <div className="field col-12 md:col-6 lg:col-4" key={field.name}>{generateInputField(field)}</div>
                                                    );
                                                }
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </AccordionTab>
                        );
                    })}
                </Accordion>
                <hr />

                <div className="text-right">
                    <Button label="Save" onClick={submitForm} disabled={!canSave} loading={formLoading} loadingIcon="pi pi-spin pi-sun" iconPos="center" />
                </div>
            </div>
        </>
    );
};

export default NewClaimManualForm;
